import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft, X } from "lucide-react";
import Turnstile, { TURNSTILE_ON, useTurnstile } from "@/components/Turnstile";
import { authClient, setPlatformToken } from "@/lib/musicApi";
import { usePlatformAuth } from "@/contexts/PlatformAuthContext";
import { trackClick, trackLogin } from "@/lib/track";
import { useJoinPrice } from "@/hooks/useJoinPrice";
import { CopyWithCoinPrice, COIN_PRICE_SLOT } from "@/components/common/CoinPrice";
import { DeviceList } from "@/components/auth/DeviceList";
import { MagicCodeEntry } from "@/components/auth/MagicCodeEntry";
import { PasswordInput } from "@/components/PasswordInput";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { formatLockUntil } from "@/lib/lockoutTime";

const fmtErr = (detail, t) => {
  const generic = t("auth.genericError");
  if (detail == null) return generic;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  if (detail?.error === "account_locked") {
    const time = formatLockUntil(detail.lockout_until, i18n.language);
    return time ? t("auth.accountLocked", { time }) : generic;
  }
  return generic;
};

// Post-login redirect (login.md table), honouring ?next.
const destinationFor = (user, next) => {
  if (next) return next;
  const status = user?.subscription_status;
  const roles = user?.roles || [];
  if (status === "active" || status === "trialing") return "/watch";
  if (roles.includes("clipper")) return "/account/clipper";
  if (status) return "/pricing"; // lapsed: had a subscription, no longer active
  return "/account/purchases";
};

const field = { background: "var(--bg-subtle)", border: "1px solid var(--border-color)", color: "var(--text-primary)" };

// Module-level so it keeps a stable identity across Login re-renders; defining
// it inside the component remounted the inputs on every keystroke (focus loss).
const Shell = ({ children, title, error, modal = false, onClose }) => {
  const card = (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className={`w-full max-w-sm rounded-2xl p-7${modal ? " relative" : ""}`} style={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)" }}>
      {modal && onClose && (
        <button type="button" onClick={onClose} data-testid="login-close" className="absolute top-4 right-4" style={{ color: "var(--text-secondary)" }} aria-label="Close"><X size={18} /></button>
      )}
      <h1 className="text-2xl font-bold mb-5" style={{ color: "var(--text-primary)" }}>{title}</h1>
      {error && <p data-testid="login-error" className="text-sm mb-3" style={{ color: "#ef4444" }}>{error}</p>}
      {children}
    </motion.div>
  );
  if (modal) return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4" style={{ background: "rgba(10,15,26,0.6)" }} data-testid="login-modal-overlay">{card}</div>
  );
  return (
    <main data-testid="login-page" className="min-h-screen flex items-center justify-center px-4 pt-20 pb-16" style={{ background: "var(--bg-primary)" }}>{card}</main>
  );
};

export default function Login({ modal = false, onSuccess, onClose } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { refresh, platformUser, loading: authLoading } = usePlatformAuth();
  const { joinCoins } = useJoinPrice();
  const next = new URLSearchParams(location.search).get("next");

  const [step, setStep] = useState("credentials"); // credentials | twofa | devices | forgot | linksent
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [totp, setTotp] = useState("");
  const [devices, setDevices] = useState({ revoke_token: "", sessions: [] });
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotToken, setForgotToken] = useState("");
  const [forgotCaptchaErr, setForgotCaptchaErr] = useState("");
  const [pendingId, setPendingId] = useState("");

  const finish = useCallback(async (data) => {
    if (data.token) setPlatformToken(data.token);
    if (data.token) trackLogin("password");
    await refresh();
    if (modal) { onSuccess && onSuccess(); return; }
    navigate(destinationFor(data.user, next), { replace: true });
  }, [refresh, navigate, next, modal, onSuccess]);

  // Already signed in: never show the logged-out form. Wait for the auth context to
  // resolve first (no flash), then redirect honouring ?next via destinationFor. The
  // modal login is contextual (used in place) so it does not redirect.
  useEffect(() => {
    if (modal || authLoading || !platformUser) return;
    navigate(destinationFor(platformUser, next), { replace: true });
  }, [modal, authLoading, platformUser, next, navigate]);

  const submitCredentials = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try {
      const { data } = await authClient.post("/subscriber/login", { email: email.toLowerCase(), password });
      if (data.requires_2fa) { setTempToken(data.temp_token); setStep("twofa"); setBusy(false); return; }
      await finish(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.error === "session_limit_reached") { setDevices(d); setStep("devices"); }
      else setError(fmtErr(d, t));
      setBusy(false);
    }
  };

  // Passwordless sign-in link (reuses the shopper magic-link pattern) so shadow
  // accounts that never set a password can still get in now that Google is gone.
  const tsMagic = useTurnstile("magic_link");
  const sendLink = async () => {
    if (!email) { setError(t("auth.enterEmailFirst")); return; }
    setError(""); setBusy(true);
    try {
      const { data } = await authClient.post("/magic-link", { email: email.toLowerCase(), turnstile_token: await tsMagic() });
      setPendingId(data?.pending_id || "");
    } catch { /* generic, no enumeration */ }
    setStep("linksent"); setBusy(false);
  };

  const submit2fa = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try {
      const { data } = await authClient.post("/subscriber/login-2fa", { temp_token: tempToken, totp_code: totp });
      if (data?.detail?.error === "session_limit_reached") { setDevices(data.detail); setStep("devices"); setBusy(false); return; }
      await finish(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.error === "session_limit_reached") { setDevices(d); setStep("devices"); }
      else setError(fmtErr(d, t));
      setBusy(false);
    }
  };

  const revokeDevice = async (jti) => {
    setBusy(true); setError("");
    try {
      await authClient.post("/session-removal/request", { revoke_token: devices.revoke_token, jti });
      // Remember our revoke_token so, if the email link is opened in THIS browser,
      // the confirm page can prove same-device and sign us in there instead.
      localStorage.setItem("samtwo_pending_removal", devices.revoke_token);
      setDevices((d) => ({ ...d, sent: true }));
    } catch (err) { setError(fmtErr(err?.response?.data?.detail, t)); }
    setBusy(false);
  };

  // While waiting in the "check your email" state, poll to finish the sign-in this
  // device already started, the moment the emailed link frees a slot. No password
  // retyped; only this session (holding the revoke_token) can claim its grant.
  useEffect(() => {
    if (step !== "devices" || !devices.sent || !devices.revoke_token) return;
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await authClient.post("/session-complete", { revoke_token: devices.revoke_token });
        if (stop || data.status !== "ready") return;
        stop = true;
        localStorage.removeItem("samtwo_pending_removal");
        await finish(data);
      } catch (err) {
        if (err?.response?.status === 401) {
          stop = true;
          localStorage.removeItem("samtwo_pending_removal");
          setError(t("auth.signInExpired"));
          setDevices({ revoke_token: "", sessions: [] });
          setStep("credentials");
        }
      }
    };
    const id = setInterval(poll, 2500);
    poll();
    return () => { stop = true; clearInterval(id); };
  }, [step, devices.sent, devices.revoke_token, finish]);

  // If the waiting tab is reloaded (or reopened in the same browser), resume the
  // "check your email" polling from the stored revoke_token so the pending sign-in
  // isn't lost. It still dies with the token: an expired one makes the poll 401.
  useEffect(() => {
    const pending = localStorage.getItem("samtwo_pending_removal");
    if (pending) { setDevices({ revoke_token: pending, sessions: [], sent: true }); setStep("devices"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitForgot = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try {
      await authClient.post("/subscriber/forgot-password", { email: email.toLowerCase(), turnstile_token: forgotToken });
      setForgotSent(true);
    } catch (err) { setError(fmtErr(err?.response?.data?.detail, t)); }
    setBusy(false);
  };

  if (!modal && (authLoading || platformUser)) return (
    <main data-testid="login-redirecting" className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-primary)" }}>
      <Loader2 size={28} className="animate-spin" style={{ color: "var(--accent-primary)" }} />
    </main>
  );

  if (step === "twofa") return (
    <Shell title={t("auth.twofaTitle")} error={error} modal={modal} onClose={onClose}>
      <form onSubmit={submit2fa}>
        <p className="text-base font-semibold mb-3" style={{ color: "var(--text-secondary)" }}>{t("auth.twofaPrompt")}</p>
        <input value={totp} onChange={(e) => setTotp(e.target.value)} inputMode="numeric" maxLength={6} placeholder="123456" data-testid="login-2fa-code"
          className="w-full px-4 py-3 rounded-lg text-sm mb-4 outline-none tracking-widest text-center" style={field} />
        <button type="submit" disabled={busy} data-testid="login-2fa-submit" className="w-full py-3 rounded-full text-sm font-semibold" style={{ background: "var(--accent-primary)", color: "#fff", opacity: busy ? 0.6 : 1 }}>{busy ? "..." : t("auth.verify")}</button>
      </form>
    </Shell>
  );

  if (step === "devices") return (
    <Shell title={t("auth.tooManyTitle")} error={error} modal={modal} onClose={onClose}>
      {devices.sent ? (
        <p data-testid="device-removal-sent" className="text-base font-semibold" style={{ color: "var(--text-secondary)" }}>
          {t("auth.deviceRemovalSent")}
        </p>
      ) : (
        <>
          <p className="text-base mb-3" style={{ color: "var(--text-secondary)" }}>{t("auth.deviceLimit")}</p>
          <DeviceList sessions={devices.sessions} onRevoke={revokeDevice} busy={busy} />
        </>
      )}
      <button onClick={() => { localStorage.removeItem("samtwo_pending_removal"); setStep("credentials"); setError(""); setDevices({ revoke_token: "", sessions: [] }); }} className="mt-4 text-sm font-semibold flex items-center gap-1" style={{ color: "var(--text-secondary)" }}><ArrowLeft size={12} /> {t("auth.back")}</button>
    </Shell>
  );

  if (step === "forgot") return (
    <Shell title={t("auth.forgotTitle")} error={error} modal={modal} onClose={onClose}>
      {forgotSent ? (
        <div data-testid="forgot-sent">
          <p className="text-base font-semibold mb-4" style={{ color: "var(--text-secondary)" }}>{t("auth.forgotSent")}</p>
          <button onClick={() => { setStep("credentials"); setForgotSent(false); }} data-testid="forgot-back" className="text-sm font-semibold" style={{ color: "var(--accent-primary)" }}>{t("auth.backToLogin")}</button>
        </div>
      ) : (
        <form onSubmit={submitForgot}>
          <p className="text-base mb-3" style={{ color: "var(--text-secondary)" }}>{t("auth.forgotPrompt")}</p>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("auth.emailPlaceholder")} data-testid="forgot-email" autoComplete="email"
            className="w-full px-4 py-3 rounded-lg text-sm mb-2 outline-none" style={field} />
          <Turnstile action="forgot" onVerify={setForgotToken} onError={setForgotCaptchaErr} />
          {TURNSTILE_ON && !forgotToken && (
            <p className="text-xs mb-2" data-testid="forgot-captcha-status" style={{ color: forgotCaptchaErr ? "#ef4444" : "var(--text-secondary)" }}>
              {forgotCaptchaErr ? t("auth.captchaFailed", { code: forgotCaptchaErr }) : t("auth.captchaWaiting")}
            </p>
          )}
          <button type="submit" disabled={busy || (TURNSTILE_ON && !forgotToken)} data-testid="forgot-submit" className="w-full mt-2 py-3 rounded-full text-sm font-semibold" style={{ background: "var(--accent-primary)", color: "#fff", opacity: busy || (TURNSTILE_ON && !forgotToken) ? 0.5 : 1 }}>{busy ? "..." : t("auth.sendResetLink")}</button>
          <button type="button" onClick={() => { setStep("credentials"); setError(""); }} className="mt-3 w-full text-sm font-semibold flex items-center justify-center gap-1" style={{ color: "var(--text-secondary)" }}><ArrowLeft size={12} /> {t("auth.backToLogin")}</button>
        </form>
      )}
    </Shell>
  );

  if (step === "linksent") return (
    <Shell title={t("auth.checkEmailTitle")} modal={modal} onClose={onClose}>
      <div data-testid="login-link-sent">
        <p className="text-base font-semibold mb-4" style={{ color: "var(--text-secondary)" }}>{t("auth.magicLinkSent")}</p>
        {pendingId && <MagicCodeEntry pendingId={pendingId} onSuccess={finish} />}
        <button onClick={() => { setStep("credentials"); setError(""); }} data-testid="login-link-back" className="mt-5 text-sm font-semibold flex items-center gap-1" style={{ color: "var(--accent-primary)" }}><ArrowLeft size={13} /> {t("auth.backToLogin")}</button>
      </div>
    </Shell>
  );

  return (
    <Shell title={t("auth.loginTitle")} error={error} modal={modal} onClose={onClose}>
      <form onSubmit={submitCredentials}>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("auth.emailPlaceholder")} data-testid="login-email" autoComplete="email"
          className="w-full px-4 py-3 rounded-lg text-sm mb-3 outline-none" style={field} />
        <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("auth.passwordPlaceholder")} data-testid="login-password" autoComplete="current-password"
          wrapperClassName="relative mb-2" className="w-full px-4 py-3 rounded-lg text-sm outline-none" style={field} />
        <button type="button" onClick={() => { setStep("forgot"); setError(""); }} data-testid="login-forgot-link" className="text-sm font-semibold mb-4 block" style={{ color: "var(--accent-primary)" }}>{t("auth.forgotLink")}</button>
        <button type="submit" disabled={busy} data-testid="login-submit" className="w-full py-3 rounded-full text-sm font-semibold" style={{ background: "var(--accent-primary)", color: "#fff", opacity: busy ? 0.6 : 1 }}>{busy ? "..." : t("auth.loginButton")}</button>
      </form>
      <div className="flex items-center gap-3 my-4">
        <span className="flex-1 h-px" style={{ background: "var(--border-color)" }} /><span className="text-sm" style={{ color: "var(--text-secondary)" }}>{t("auth.or")}</span><span className="flex-1 h-px" style={{ background: "var(--border-color)" }} />
      </div>
      <button type="button" onClick={sendLink} disabled={busy} data-testid="login-magic" className="w-full py-3 rounded-full text-sm font-medium" style={{ border: "1px solid var(--border-color)", color: "var(--text-primary)" }}>{t("auth.emailSignInLink")}</button>
      <p className="text-sm text-center mt-3" style={{ color: "var(--text-secondary)" }}>{t("auth.noPasswordHint")}</p>
      <p className="text-sm text-center mt-5" style={{ color: "var(--text-secondary)" }}>
        {t("auth.newHere")} <a href="/pricing" data-testid="login-signup-link" className="font-semibold" style={{ color: "var(--accent-primary)" }}><CopyWithCoinPrice template={t("auth.joinFor", { price: COIN_PRICE_SLOT })} coins={joinCoins} size="sm" /></a>
      </p>
    </Shell>
  );
}
